import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/helpers/cn.helper';

type MenuAlign = 'left' | 'right';

interface MenuContextValue {
  readonly close: () => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext(): MenuContextValue {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('MenuItem doit être utilisé dans un Menu.');
  return ctx;
}

export interface MenuProps {
  readonly label: string;
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  readonly align?: MenuAlign;
  readonly className?: string;
  readonly buttonClassName?: string;
}

/** Menu déroulant sobre — contrôle l'ouverture, Escape et le clic extérieur. */
export function Menu({
  label,
  trigger,
  children,
  align = 'right',
  className,
  buttonClassName,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const close = () => setOpen(false);
  const toggle = () => setOpen((value) => !value);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && rootRef.current && !rootRef.current.contains(target)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
        className={buttonClassName}
      >
        {trigger}
      </button>
      {open ? (
        <MenuContext.Provider value={{ close }}>
          <div
            role="menu"
            className={cn(
              'absolute z-40 mt-1 min-w-44 overflow-hidden rounded-md border border-hairline bg-surface-2 py-1 shadow-pop',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {children}
          </div>
        </MenuContext.Provider>
      ) : null}
    </div>
  );
}

export interface MenuItemProps {
  readonly children: ReactNode;
  readonly onSelect?: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export function MenuItem({ children, onSelect, danger = false, disabled = false }: MenuItemProps) {
  const { close } = useMenuContext();
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        close();
        onSelect?.();
      }}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-body transition-colors',
        danger
          ? 'text-danger hover:bg-danger-soft disabled:text-ink-faint'
          : 'text-ink hover:bg-surface-3 disabled:text-ink-faint',
      )}
    >
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div aria-hidden="true" className="my-1 border-t border-hairline" />;
}
